export function generateShortSnippet(
  selectedLang: string,
  operation: any,
  example: any,
): string {
  switch (selectedLang) {
    case "java":
      return generateCodeSnippetJava(operation, example, false).trimEnd();
    case "js":
      return generateCodeSnippetJavascript(operation, example, false).trimEnd();
    case "python":
      return generateCodeSnippetPython(operation, example, false).trimEnd();
  }
  return "";
}

export function generateFullSnippet(
  selectedLang: string,
  operation: any,
  example: any,
): string {
  switch (selectedLang) {
    case "java":
      return generateCodeSnippetJava(operation, example, true).trimEnd();
    case "js":
      return generateCodeSnippetJavascript(operation, example, true).trimEnd();
    case "python":
      return generateCodeSnippetPython(operation, example, true).trimEnd();
  }
  return "";
}

export function generateCapabilitiesSnippet(selectedLang: string): string {
  switch (selectedLang) {
    case "java":
      return `import com.regexsolver.api.RegexSolverClient;
import com.regexsolver.api.Term;
import java.util.List;

public class Main {
    public static void main(String[] args) {
        RegexSolverClient client = RegexSolverClient.builder()
            .apiToken("REGEXSOLVER_API_TOKEN")
            .build();

        // 1. Define a complex base policy (e.g., any internal API path)
        Term internalApi = Term.regex("/api/v[12]/[a-z]+(/.*)?");

        // 2. Define forbidden patterns (e.g., no 'admin' or 'debug' segments)
        Term forbidden = Term.regex(".*/(admin|debug|private)/.*");

        // 3. Compute 'Set Difference': Allow the API but EXCLUDE any forbidden paths
        // This is mathematically complex to write as a single regex manually!
        Term securePolicy = client.difference(internalApi, forbidden);

        // 4. Analyze the security of the new policy
        System.out.println("Secure Pattern: " + client.getPattern(securePolicy));
        System.out.println("Is it empty? " + client.isEmpty(securePolicy)); // Verify it still allows something

        // 5. Generate 'Safe' test cases that are guaranteed NOT to hit forbidden paths
        List<String> safePaths = client.generateStrings(securePolicy, 5, 0);
        System.out.println("Safe Test Paths: " + safePaths);

        // 6. Fast client-side enforcement
        System.out.println("Allow '/api/v1/users'? " + securePolicy.matches("/api/v1/users"));   // true
        System.out.println("Allow '/api/v1/admin'? " + securePolicy.matches("/api/v1/admin/")); // false
    }
}`;
    case "js":
      return `import { RegexSolverClient, Term } from 'regexsolver';

const client = new RegexSolverClient({
  apiToken: 'REGEXSOLVER_API_TOKEN',
});

// 1. Define a complex base policy (e.g., any internal API path)
const internalApi = Term.regex("/api/v[12]/[a-z]+(/.*)?");

// 2. Define forbidden patterns (e.g., no 'admin' or 'debug' segments)
const forbidden = Term.regex(".*/(admin|debug|private)/.*");

// 3. Compute 'Set Difference': Allow the API but EXCLUDE any forbidden paths
// This is mathematically complex to write as a single regex manually!
const securePolicy = await client.difference(internalApi, forbidden);

// 4. Analyze the security of the new policy
console.log("Secure Pattern:", await client.getPattern(securePolicy));
console.log("Is it empty?", await client.isEmpty(securePolicy));

// 5. Generate 'Safe' test cases that are guaranteed NOT to hit forbidden paths
const safePaths = await client.generateStrings(securePolicy, 5, 0);
console.log("Safe Test Paths:", safePaths);

// 6. Fast client-side enforcement
console.log("Allow '/api/v1/users'?", securePolicy.matches("/api/v1/users"));   // true
console.log("Allow '/api/v1/admin'?", securePolicy.matches("/api/v1/admin/")); // false`;
    case "python":
      return `from regexsolver import RegexSolverClient, Term

client = RegexSolverClient("REGEXSOLVER_API_TOKEN")

# 1. Define a complex base policy (e.g., any internal API path)
internal_api = Term.regex(r"/api/v[12]/[a-z]+(/.*)?")

# 2. Define forbidden patterns (e.g., no 'admin' or 'debug' segments)
forbidden = Term.regex(r".*/(admin|debug|private)/.*")

# 3. Compute 'Set Difference': Allow the API but EXCLUDE any forbidden paths
# This is mathematically complex to write as a single regex manually!
secure_policy = client.difference(internal_api, forbidden)

# 4. Analyze the security of the new policy
print(f"Secure Pattern: {client.get_pattern(secure_policy)}")
print(f"Is it empty? {client.is_empty(secure_policy)}")

# 5. Generate 'Safe' test cases that are guaranteed NOT to hit forbidden paths
safe_paths = client.generate_strings(secure_policy, 5, 0)
print(f"Safe Test Paths: {safe_paths}")

# 6. Fast client-side enforcement
print(f"Allow '/api/v1/users'? {secure_policy.matches('/api/v1/users')}")   # True
print(f"Allow '/api/v1/admin'? {secure_policy.matches('/api/v1/admin/')}") # False`;
  }
  return "";
}

/**
 * @deprecated Use generateShortSnippet or generateFullSnippet
 */
export function generateCodeSnippet(
  selectedLang: string,
  operation: any,
  example: any,
): string {
  return generateShortSnippet(selectedLang, operation, example);
}

// --- Common Helpers ---

function escapeValue(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

// generateStrings options: emitted only when present in the example, so
// snippets never render `undefined` for absent fields.
const STRINGS_OPTION_KEYS = [
  "pathOrder",
  "characterOrder",
  "seed",
  "minLength",
  "maxLength",
  "charset",
] as const;
const STRING_TYPED_OPTIONS: readonly string[] = [
  "pathOrder",
  "characterOrder",
  "charset",
];
const PYTHON_OPTION_KEYS: Record<string, string> = {
  pathOrder: "path_order",
  characterOrder: "character_order",
  seed: "seed",
  minLength: "min_length",
  maxLength: "max_length",
  charset: "charset",
};

function stringsOptions(example: any): Array<[string, unknown]> {
  return STRINGS_OPTION_KEYS.filter(
    (k) => example?.[k] !== undefined && example?.[k] !== null,
  ).map((k) => [k, example[k]]);
}

function javaStringsOption(key: string, value: unknown): string {
  switch (key) {
    case "pathOrder":
      return `.pathOrder(PathOrder.${String(value).toUpperCase()})`;
    case "characterOrder":
      return `.characterOrder(CharacterOrder.${String(value).toUpperCase()})`;
    case "seed":
      return `.seed(${value}L)`;
    case "charset":
      return `.charset("${escapeValue(String(value))}")`;
    default:
      return `.${key}(${value})`;
  }
}

// --- Java ---

function generateTermJava(name: string, type: string, value: string): string {
  return `Term ${name} = Term.${type}("${escapeValue(value)}");\n`;
}

function generateTermsVarsAndArgsJava(example: any) {
  let vars = "";
  let args = "";
  if (example?.terms) {
    const names: string[] = [];
    example.terms.forEach((t: any, i: number) => {
      const name = `term${i + 1}`;
      vars += generateTermJava(name, t.type, t.value);
      names.push(name);
    });
    args = names.join(", ");
  } else if (example?.term) {
    vars += generateTermJava("term", example.term.type, example.term.value);
    args = "term";
  }
  return { vars, args };
}

function wrapJavaCall(call: string, full: boolean): string {
  return full ? `System.out.println(${call});` : `${call};`;
}

function generateCodeSnippetJava(operation: any, example: any, full: boolean): string {
  let snippet = "";
  const indent = full ? "        " : "";
  const stringsOpts =
    operation.operationId === "strings" ? stringsOptions(example) : [];

  if (full) {
    snippet += "import com.regexsolver.api.RegexSolverClient;\n";
    snippet += "import com.regexsolver.api.Term;\n";
    if (stringsOpts.length > 0) {
      snippet += "import com.regexsolver.api.GenerateStringsOptions;\n";
      if (stringsOpts.some(([k]) => k === "pathOrder"))
        snippet += "import com.regexsolver.api.PathOrder;\n";
      if (stringsOpts.some(([k]) => k === "characterOrder"))
        snippet += "import com.regexsolver.api.CharacterOrder;\n";
    }
    snippet += "\n";
    snippet += "public class Main {\n";
    snippet += "    public static void main(String[] args) {\n";
    snippet += "        RegexSolverClient client = RegexSolverClient.builder()\n";
    snippet += '            .apiToken("REGEXSOLVER_API_TOKEN")\n';
    snippet += "            .build();\n\n";
  }

  const { vars, args } = generateTermsVarsAndArgsJava(example);
  if (full) {
    snippet += vars.split("\n").map(line => line.length > 0 ? indent + line : "").join("\n");
  } else {
    snippet += vars;
  }
  if (vars.length > 0) snippet += "\n";

  let pre = "";
  let call = "";
  switch (operation.operationId) {
    case "cardinality":
      call = wrapJavaCall(`client.getCardinality(${args})`, full);
      break;
    case "dot":
      call = wrapJavaCall(`client.getDot(${args})`, full);
      break;
    case "empty":
      call = wrapJavaCall(`client.isEmpty(${args})`, full);
      break;
    case "empty_string":
      call = wrapJavaCall(`client.isEmptyString(${args})`, full);
      break;
    case "total":
      call = wrapJavaCall(`client.isTotal(${args})`, full);
      break;
    case "deterministic":
      call = wrapJavaCall(`client.isDeterministic(${args})`, full);
      break;
    case "equivalent":
      call = wrapJavaCall(`client.equivalent(${args})`, full);
      break;
    case "length":
      call = wrapJavaCall(`client.getLength(${args})`, full);
      break;
    case "pattern":
      call = wrapJavaCall(`client.getPattern(${args})`, full);
      break;
    case "subset":
      call = wrapJavaCall(`client.subset(${args})`, full);
      break;
    case "concat":
      call = wrapJavaCall(`client.concat(${args})`, full);
      break;
    case "difference":
      call = wrapJavaCall(`client.difference(${args})`, full);
      break;
    case "intersection":
      call = wrapJavaCall(`client.intersection(${args})`, full);
      break;
    case "repeat": {
      const maxArg =
        example.max !== undefined && example.max !== null
          ? `, ${example.max}`
          : `, null`;
      call = wrapJavaCall(`client.repeat(${args}, ${example.min}${maxArg})`, full);
      break;
    }
    case "union":
      call = wrapJavaCall(`client.union(${args})`, full);
      break;
    case "complement":
      call = wrapJavaCall(`client.complement(${args})`, full);
      break;
    case "determinize":
      call = wrapJavaCall(`client.determinize(${args})`, full);
      break;
    case "strings": {
      let callArgs = `${args}, ${example?.limit ?? 10}, ${example?.offset ?? 0}`;
      if (stringsOpts.length > 0) {
        pre =
          "GenerateStringsOptions options = GenerateStringsOptions.builder()\n" +
          stringsOpts
            .map(([k, v]) => `        ${javaStringsOption(k, v)}`)
            .join("\n") +
          ";";
        callArgs += ", options";
      }
      call = wrapJavaCall(`client.generateStrings(${callArgs})`, full);
      break;
    }
    case "limits":
      call = wrapJavaCall(`client.getAccountLimits()`, full);
      break;
  }

  if (pre.length > 0) {
    snippet +=
      pre
        .split("\n")
        .map((line) => indent + line)
        .join("\n") + "\n\n";
  }
  snippet += indent + call + "\n";

  if (full) {
    snippet += "    }\n";
    snippet += "}\n";
  }

  return snippet;
}

// --- JavaScript ---

function generateTermJavascript(
  name: string,
  type: string,
  value: string,
): string {
  return `const ${name} = Term.${type}("${escapeValue(value)}");\n`;
}

function generateTermsVarsAndArgsJavascript(example: any) {
  let vars = "";
  let args = "";
  if (example?.terms) {
    const names: string[] = [];
    example.terms.forEach((t: any, i: number) => {
      const name = `term${i + 1}`;
      vars += generateTermJavascript(name, t.type, t.value);
      names.push(name);
    });
    args = names.join(", ");
  } else if (example?.term) {
    vars += generateTermJavascript(
      "term",
      example.term.type,
      example.term.value,
    );
    args = "term";
  }
  return { vars, args };
}

function wrapJavascriptCall(call: string, full: boolean): string {
  return full ? `console.log(${call});` : `${call};`;
}

function generateCodeSnippetJavascript(operation: any, example: any, full: boolean): string {
  let snippet = "";

  if (full) {
    snippet += "import { RegexSolverClient, Term } from 'regexsolver';\n\n";
    snippet += "const client = new RegexSolverClient({\n";
    snippet += "  apiToken: 'REGEXSOLVER_API_TOKEN',\n";
    snippet += "});\n\n";
  }

  const { vars, args } = generateTermsVarsAndArgsJavascript(example);
  snippet += vars;
  if (vars.length > 0) snippet += "\n";

  let call = "";
  switch (operation.operationId) {
    case "cardinality":
      call = wrapJavascriptCall(`await client.getCardinality(${args})`, full);
      break;
    case "dot":
      call = wrapJavascriptCall(`await client.getDot(${args})`, full);
      break;
    case "empty":
      call = wrapJavascriptCall(`await client.isEmpty(${args})`, full);
      break;
    case "empty_string":
      call = wrapJavascriptCall(`await client.isEmptyString(${args})`, full);
      break;
    case "total":
      call = wrapJavascriptCall(`await client.isTotal(${args})`, full);
      break;
    case "deterministic":
      call = wrapJavascriptCall(`await client.isDeterministic(${args})`, full);
      break;
    case "equivalent":
      call = wrapJavascriptCall(`await client.equivalent(${args})`, full);
      break;
    case "length":
      call = wrapJavascriptCall(`await client.getLength(${args})`, full);
      break;
    case "pattern":
      call = wrapJavascriptCall(`await client.getPattern(${args})`, full);
      break;
    case "subset":
      call = wrapJavascriptCall(`await client.subset(${args})`, full);
      break;
    case "concat":
      call = wrapJavascriptCall(`await client.concat(${args})`, full);
      break;
    case "difference":
      call = wrapJavascriptCall(`await client.difference(${args})`, full);
      break;
    case "intersection":
      call = wrapJavascriptCall(`await client.intersection(${args})`, full);
      break;
    case "repeat": {
      const maxArgJs =
        example.max !== undefined && example.max !== null
          ? `, ${example.max}`
          : `, null`;
      call = wrapJavascriptCall(`await client.repeat(${args}, ${example.min}${maxArgJs})`, full);
      break;
    }
    case "union":
      call = wrapJavascriptCall(`await client.union(${args})`, full);
      break;
    case "complement":
      call = wrapJavascriptCall(`await client.complement(${args})`, full);
      break;
    case "determinize":
      call = wrapJavascriptCall(`await client.determinize(${args})`, full);
      break;
    case "strings": {
      let callArgs = `${args}, ${example?.limit ?? 10}, ${example?.offset ?? 0}`;
      const opts = stringsOptions(example);
      if (opts.length > 0) {
        const rendered = opts
          .map(([k, v]) =>
            STRING_TYPED_OPTIONS.includes(k)
              ? `${k}: "${escapeValue(String(v))}"`
              : `${k}: ${v}`,
          )
          .join(", ");
        callArgs += `, { ${rendered} }`;
      }
      call = wrapJavascriptCall(`await client.generateStrings(${callArgs})`, full);
      break;
    }
    case "limits":
      call = wrapJavascriptCall(`await client.getAccountLimits()`, full);
      break;
  }

  snippet += call + "\n";

  return snippet;
}

// --- Python ---

function generateTermPython(name: string, type: string, value: string): string {
  if (type === "regex") {
    // A raw string cannot end with an odd number of backslashes
    const isSafeForRaw = !value.endsWith("\\");

    if (isSafeForRaw) {
      if (!value.includes('"')) {
        return `${name} = Term.regex(r"${value}")\n`;
      }
      if (!value.includes("'")) {
        return `${name} = Term.regex(r'${value}')\n`;
      }
      if (!value.includes('"""')) {
        return `${name} = Term.regex(r"""${value}""")\n`;
      }
    }
  }

  // Fallback to standard escaping if raw strings are unsafe or contain all delimiters
  return `${name} = Term.${type}("${escapeValue(value)}")\n`;
}

function generateTermsVarsAndArgsPython(example: any) {
  let vars = "";
  let args = "";
  if (example?.terms) {
    const names: string[] = [];
    example.terms.forEach((t: any, i: number) => {
      const name = `term${i + 1}`;
      vars += generateTermPython(name, t.type, t.value);
      names.push(name);
    });
    args = names.join(", ");
  } else if (example?.term) {
    vars += generateTermPython("term", example.term.type, example.term.value);
    args = "term";
  }
  return { vars, args };
}

function wrapPythonCall(call: string, full: boolean): string {
  return full ? `print(${call})` : call;
}

function generateCodeSnippetPython(operation: any, example: any, full: boolean): string {
  let snippet = "";
  if (full) {
    snippet += "from regexsolver import RegexSolverClient, Term\n\n";
    snippet += 'client = RegexSolverClient("REGEXSOLVER_API_TOKEN")\n\n';
  }

  const { vars, args } = generateTermsVarsAndArgsPython(example);
  snippet += vars;
  if (vars.length > 0) snippet += "\n";

  let call = "";
  switch (operation.operationId) {
    case "cardinality":
      call = wrapPythonCall(`client.get_cardinality(${args})`, full);
      break;
    case "dot":
      call = wrapPythonCall(`client.get_dot(${args})`, full);
      break;
    case "empty":
      call = wrapPythonCall(`client.is_empty(${args})`, full);
      break;
    case "empty_string":
      call = wrapPythonCall(`client.is_empty_string(${args})`, full);
      break;
    case "total":
      call = wrapPythonCall(`client.is_total(${args})`, full);
      break;
    case "deterministic":
      call = wrapPythonCall(`client.is_deterministic(${args})`, full);
      break;
    case "equivalent":
      call = wrapPythonCall(`client.equivalent(${args})`, full);
      break;
    case "length":
      call = wrapPythonCall(`client.get_length(${args})`, full);
      break;
    case "pattern":
      call = wrapPythonCall(`client.get_pattern(${args})`, full);
      break;
    case "subset":
      call = wrapPythonCall(`client.subset(${args})`, full);
      break;
    case "concat":
      call = wrapPythonCall(`client.concat(${args})`, full);
      break;
    case "difference":
      call = wrapPythonCall(`client.difference(${args})`, full);
      break;
    case "intersection":
      call = wrapPythonCall(`client.intersection(${args})`, full);
      break;
    case "repeat": {
      const maxArgPy =
        example.max !== undefined && example.max !== null
          ? `, ${example.max}`
          : `, None`;
      call = wrapPythonCall(`client.repeat(${args}, ${example.min}${maxArgPy})`, full);
      break;
    }
    case "union":
      call = wrapPythonCall(`client.union(${args})`, full);
      break;
    case "complement":
      call = wrapPythonCall(`client.complement(${args})`, full);
      break;
    case "determinize":
      call = wrapPythonCall(`client.determinize(${args})`, full);
      break;
    case "strings": {
      let callArgs = `${args}, ${example?.limit ?? 10}, ${example?.offset ?? 0}`;
      for (const [k, v] of stringsOptions(example)) {
        callArgs += STRING_TYPED_OPTIONS.includes(k)
          ? `, ${PYTHON_OPTION_KEYS[k]}="${escapeValue(String(v))}"`
          : `, ${PYTHON_OPTION_KEYS[k]}=${v}`;
      }
      call = wrapPythonCall(`client.generate_strings(${callArgs})`, full);
      break;
    }
    case "limits":
      call = wrapPythonCall(`client.get_account_limits()`, full);
      break;
  }

  snippet += call + "\n";
  return snippet;
}
